import { useEffect, useRef, useState } from 'react';
import { getJson } from '../api/client';
import { Field, Select } from '../components/form';
import { Alert, Badge, Button, Card, EmptyState, Spinner } from '../components/ui';
import { dataBr, mesAno } from '../lib/format';
import { navigate } from '../router';
import { type Lote, STATUS_LOTE } from './Conferencia';
import { type Fechamento, PainelFechamento } from './ConferenciaLote';
import { type Rascunho, Secoes } from './RascunhoBalancete';
import styles from './screens.module.css';

interface Competencia {
  id: number;
  ano: number;
  mes: number;
}
type CargaRascunho =
  | { estado: 'carregando' }
  | { estado: 'erro' }
  | { estado: 'ok'; rascunho: Rascunho };

/**
 * Relatórios do mês (RF-D01–D05) para contábil e síndico, só leitura: baixa o CSV da
 * competência, mostra o fechamento do saldo dos lotes do extrato e o rascunho do balancete.
 * Não há ação de escrita (o `contabilReadOnly` do servidor continua barrando). Cada bloco
 * só aparece com o módulo ligado (`banco`, `balancete`), como a API gateia as rotas.
 */
export function RelatorioDoMes({ banco, balancete }: { banco: boolean; balancete: boolean }) {
  const [comps, setComps] = useState<Competencia[] | 'erro' | null>(null);
  const [sel, setSel] = useState('');
  const [lotes, setLotes] = useState<Lote[] | 'erro' | null>(null);
  const [rascunho, setRascunho] = useState<CargaRascunho>({ estado: 'carregando' });
  // Competência selecionada no momento: respostas de outra (troca rápida) são descartadas.
  const selAtual = useRef(sel);
  selAtual.current = sel;

  useEffect(() => {
    void getJson<{ competencias: Competencia[] }>('/api/competencias').then((d) => {
      if (!d) {
        setComps('erro');
        return;
      }
      setComps(d.competencias);
      if (d.competencias[0]) setSel(String(d.competencias[0].id));
    });
    if (banco) {
      void getJson<{ extratos: Lote[] }>('/api/extratos').then((d) =>
        setLotes(d ? d.extratos : 'erro'),
      );
    }
  }, [banco]);

  useEffect(() => {
    if (!sel || !balancete) return;
    setRascunho({ estado: 'carregando' });
    void getJson<{ rascunho: Rascunho }>(
      `/api/competencias/${encodeURIComponent(sel)}/balancete/rascunho`,
    ).then((d) => {
      if (selAtual.current !== sel) return;
      setRascunho(d ? { estado: 'ok', rascunho: d.rascunho } : { estado: 'erro' });
    });
  }, [sel, balancete]);

  if (comps === null) return <Spinner />;
  if (comps === 'erro')
    return <Alert kind="danger">Não foi possível carregar os relatórios.</Alert>;
  if (comps.length === 0) return <EmptyState>Nenhuma competência aberta.</EmptyState>;

  const competencia = comps.find((c) => String(c.id) === sel);
  const rotulo = competencia ? mesAno(competencia) : '';
  const lotesDaCompetencia = Array.isArray(lotes)
    ? lotes.filter((l) => String(l.competenciaId) === sel)
    : [];

  return (
    <div className={styles.stack}>
      <Field label="Competência">
        {(id) => (
          <Select
            id={id}
            value={sel}
            onChange={(e) => setSel(e.target.value)}
            style={{ maxWidth: '12rem' }}
          >
            {comps.map((c) => (
              <option key={c.id} value={c.id}>
                {mesAno(c)}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Card title="Exportar o mês">
        <p>
          {`CSV com os lançamentos, a arrecadação por unidade e os fundos de ${rotulo} (separador ponto e vírgula).`}
        </p>
        <a
          href={`/api/competencias/${encodeURIComponent(sel)}/export.csv`}
          download
          aria-label={`Baixar CSV de ${rotulo}`}
        >
          Baixar CSV
        </a>
      </Card>

      {banco ? (
        <Card title="Fechamento do extrato">
          {lotes === null ? <Spinner /> : null}
          {lotes === 'erro' ? (
            <Alert kind="danger">Não foi possível carregar os extratos.</Alert>
          ) : null}
          {Array.isArray(lotes) && lotesDaCompetencia.length === 0 ? (
            <EmptyState>Nenhum extrato nesta competência.</EmptyState>
          ) : null}
          {lotesDaCompetencia.length > 0 ? (
            <ul className={styles.list}>
              {lotesDaCompetencia.map((l) => (
                <li
                  key={l.id}
                  className={styles.listItem}
                  style={{ cursor: 'default', display: 'block' }}
                >
                  <div className={styles.row}>
                    <span>Lote {l.id}</span>
                    <span>
                      {l.periodoInicio && l.periodoFim
                        ? `${dataBr(l.periodoInicio)} a ${dataBr(l.periodoFim)}`
                        : 'Sem período'}
                    </span>
                    <Badge tone={l.status === 'conferido' ? 'success' : undefined}>
                      {STATUS_LOTE[l.status]}
                    </Badge>
                    <span>{l.lancamentos} lançamentos</span>
                  </div>
                  <FechamentoDoLote loteId={l.id} />
                </li>
              ))}
            </ul>
          ) : null}
          <Button size="sm" variant="secondary" onClick={() => navigate('/conferencia')}>
            Abrir conferência
          </Button>
        </Card>
      ) : null}

      {balancete ? (
        <Card title="Rascunho do balancete">
          {rascunho.estado === 'carregando' ? <Spinner /> : null}
          {rascunho.estado === 'erro' ? (
            <Alert kind="danger">Não foi possível carregar o rascunho do balancete.</Alert>
          ) : null}
          {rascunho.estado === 'ok' ? <Secoes rascunho={rascunho.rascunho} /> : null}
        </Card>
      ) : null}
    </div>
  );
}

/** Fechamento do saldo de um lote (saldo calculado × declarado, vindo do servidor). */
function FechamentoDoLote({ loteId }: { loteId: number }) {
  const [f, setF] = useState<Fechamento | 'erro' | null>(null);

  useEffect(() => {
    let vivo = true;
    setF(null);
    void getJson<Fechamento>(`/api/extratos/${encodeURIComponent(loteId)}/fechamento`).then((d) => {
      if (vivo) setF(d ?? 'erro');
    });
    return () => {
      vivo = false;
    };
  }, [loteId]);

  if (f === null) return <Spinner />;
  if (f === 'erro') {
    return <Alert kind="danger">Não foi possível carregar o fechamento do saldo.</Alert>;
  }
  return <PainelFechamento f={f} />;
}
